import { useCallback } from "react"

import {
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query"

import { BillingApiError } from "../client"
import type { UsageResponse } from "../types"
import { shouldRetryBillingQuery } from "../utils/billingQueryRetry"
import { useBillingClientContext } from "./useBillingClient"

interface UseBillingUsageOptions {
  enabled?: boolean
}

const BILLING_USAGE_QUERY_KEY = ["billing", "usage"] as const

function getBillingUsageQueryKey(organizationSlug: string | null) {
  return [...BILLING_USAGE_QUERY_KEY, organizationSlug] as const
}

export function useBillingUsage(
  options: UseBillingUsageOptions = {},
): UseQueryResult<UsageResponse, BillingApiError> {
  const { client, isReady, organizationSlug, routeOrganizationMissing } =
    useBillingClientContext()

  return useQuery<UsageResponse, BillingApiError>({
    queryKey: getBillingUsageQueryKey(organizationSlug),
    queryFn: () => {
      if (routeOrganizationMissing) {
        throw new BillingApiError(
          404,
          {
            error_code: "ORGANIZATION_NOT_FOUND",
            message: "Route organization not found in memberships.",
          },
          "Failed to load usage",
        )
      }
      return client.getUsage()
    },
    enabled: (options.enabled ?? true) && isReady,
    staleTime: 30_000,
    retry: shouldRetryBillingQuery,
  })
}

/**
 * Drops the cached usage for every organization so the next read goes back to
 * the server. Spending allowance — launching a run — makes the cached counts
 * wrong well inside their stale window, and surfaces that stay mounted across
 * the spend (the launcher chip) would otherwise keep showing the old band.
 */
export function useInvalidateBillingUsage(): () => Promise<void> {
  const queryClient = useQueryClient()

  return useCallback(
    () => queryClient.invalidateQueries({ queryKey: BILLING_USAGE_QUERY_KEY }),
    [queryClient],
  )
}
